import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  switchMap,
  throwError,
} from 'rxjs';
import { DialogService } from '../services/dialog.service';
import { AuthService } from 'src/app/auth/services/auth.service';


export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const dialogSvc = inject(DialogService);
  const authSvc = inject(AuthService);

  // Skip adding the token to login request and Firebase token refresh requests
  if (req.url.includes('/Security/Login') || 
      req.url.includes('securetoken.googleapis.com')) {
    return next(req);
  }

  // Get current token and add to header
  const token = authSvc.getToken();
  
  // If no token available, proceed without authorization header
  if (!token) {
    return next(req);
  }
  
  const modifiedReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });

  return next(modifiedReq).pipe(
    catchError((err: any) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        console.warn('Token expired or invalid. Attempting to refresh...');

        return authSvc.refreshIdToken().pipe(
          switchMap((res: any) => {
            // Check if refresh was successful
            if (!res) {
              throw new Error('No refresh token available');
            }
            
            console.log('Token refreshed successfully');
            
            const newIdToken = res.id_token;
            const newRefreshToken = res.refresh_token;

            if (!newIdToken) {
              throw new Error('Failed to obtain new ID token.');
            }

            // Firebase doesn't always return a new refresh token
            if (newRefreshToken) {
              authSvc.saveAuthTokens(newIdToken, newRefreshToken);
            } else {
              // Just update the ID token
              authSvc.saveIdToken(newIdToken);
            }

            // Retry the original request with new token
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newIdToken}`,
              },
            });

            return next(retryReq);
          }),
          catchError(refreshErr => {
            console.error('Token refresh failed:', refreshErr);
            authSvc.clearTokens();
            router.navigate(['/']);
            dialogSvc.errorDialog({
              message: 'Your session has expired. Please log in again.',
            }, 4);
            return throwError(() => refreshErr);
          })
        );
      }

      // Other types of HTTP errors
      const errorMessage = err?.error?.message ?? 'An unexpected error occurred.';
      console.error('HTTP Error:', err);
      dialogSvc.errorDialog({
        message: `${errorMessage} - Error ${err.status}`,
      }, 4);

      return throwError(() => err);
    })
  );
};